import React, { useState, useEffect, useRef } from "react";
import ChordPreQuiz from "./ChordPreQuiz";
import ChordQuiz, { ChordQuizOptions, ChordTypeName } from "./ChordQuiz";
import PlaybackOptionsPanel from "../../../components/PlaybackOptionsPanel";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import { useSoundLibrary } from "../../../hooks/useSoundLibrary";
import { useDrillLocalStorage } from "../../../hooks/useDrillLocalStorage";
import { usePlaybackSettings } from "../../../hooks/usePlaybackSettings";
import { Note } from "../../../soundLibrary";
import { ROUTES } from "../../../config/routes";
import { SEO_METADATA } from "../../../config/seoMetadata";
import SEO from "../../../components/SEO";
import styles from "../../../styles/ChordQuiz.module.css";
import { CHORD_TYPES } from "../../../soundLibrary";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const noteString = (noteIndex: number) => {
    const octave = Math.floor(noteIndex / 12);
    const name = NOTES[noteIndex % 12];
    return `${name}${octave}`;
};

const getRandomNoteIndex = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

const initialQuizOptions: ChordQuizOptions = {
    availableChords: Object.entries(CHORD_TYPES)
        .filter(([name]) => name === "MAJOR" || name === "MINOR")
        .map(([name, intervals]) => ({ name: name as ChordTypeName, intervals })),
    mode: "questions",
    questionLimit: 10,
    timeLimitSeconds: 60,
};

export default function ChordQuizWrapper() {
    const { playNote, playChord, stopAll } = useSoundLibrary();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const isMountedRef = useRef(true);
    const abortPlaybackRef = useRef(false);

    // Persist quiz options to localStorage
    const [quizOptions, setQuizOptions] = useDrillLocalStorage<ChordQuizOptions>(
        "drill_chord_storage",
        initialQuizOptions
    );

    // Persist playback settings to localStorage (shared across all drills)
    const [playbackOptions, setPlaybackOptions] = usePlaybackSettings();

    const [quizStarted, setQuizStarted] = useState<boolean>(false);

    const [questionCount, setQuestionCount] = useState(0);
    const [attemptedQuestions, setAttemptedQuestions] = useState(0);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(quizOptions.timeLimitSeconds || 0);
    const [feedback, setFeedback] = useState<{ message: string; color: string } | null>(null);
    const [quizEnded, setQuizEnded] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState<{
        root: Note;
        chordNotes: Note[];
        correctAnswer: ChordTypeName;
        questionOptions: ChordTypeName[];
    } | null>(null);

    const handlePlayChord = async () => {
        if (!currentQuestion) return;
        
        abortPlaybackRef.current = false;
        let activeVolume = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
        
        if (playbackOptions.playMode === "melodic") {
            for (let i = 0; i < currentQuestion.chordNotes.length; i++) {
                if (abortPlaybackRef.current || !isMountedRef.current) {
                    return;
                }
                
                playNote(playbackOptions.instrument, currentQuestion.chordNotes[i], {
                    velocity: activeVolume,
                    duration: 0.7,  // 0.7 second note duration
                });
                
                await new Promise((res) => setTimeout(res, 700));  // Wait for note to finish
                stopAll();  // Cut off the note
                await new Promise((res) => setTimeout(res, 200));  // Gap between notes
            }
        } else {
            await playChord(playbackOptions.instrument, currentQuestion.chordNotes, {
                velocity: activeVolume,
            });
        }
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            abortPlaybackRef.current = true;
            stopAll();
        };
    }, [stopAll]);

    const handleStart = () => {
        setQuizStarted(true);
        setQuestionCount(0);
        setAttemptedQuestions(0);
        setScore(0);
        setTimeLeft(quizOptions.timeLimitSeconds || 0);
        setFeedback(null);
        setCurrentQuestion(null);
    };

    const endQuiz = () => {
        setQuizEnded(true);
        setFeedback(null);
        setCurrentQuestion(null);
    };

    const generateQuestion = () => {
        if (!quizStarted || !quizOptions.availableChords.length || quizEnded) return;

        const rootIndex = getRandomNoteIndex(48, 60); // keep chords in middle range
        const chord = quizOptions.availableChords[Math.floor(Math.random() * quizOptions.availableChords.length)];

        const chordNotes = chord.intervals.map(
            (interval) => noteString(rootIndex + interval) as Note
        );

        const allChordNames = quizOptions.availableChords.map((c) => c.name) as ChordTypeName[];

        setCurrentQuestion({
            root: noteString(rootIndex) as Note,
            chordNotes,
            correctAnswer: chord.name as ChordTypeName,
            questionOptions: allChordNames,
        });
    };

    const resetQuiz = () => {
        setQuizStarted(false);
        setQuestionCount(0);
        setAttemptedQuestions(0);
        setScore(0);
        setFeedback(null);
        setQuizEnded(false);
        setCurrentQuestion(null);
        // Note: quizOptions and playbackOptions are preserved from localStorage

        // Scroll to top when resetting quiz
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const disableStart = () => {
        if (!quizOptions || !quizOptions.availableChords) return true;
        return quizOptions.availableChords.length < 2;
    };

    useEffect(() => {
        if (quizEnded) {
            // Scroll to top of page when quiz ends
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizEnded]);

    useEffect(() => {
        if (quizStarted) {
            // Scroll to top of page when quiz starts
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizStarted]);

    useEffect(() => {
        if (quizOptions.mode === "time" && timeLeft > 0 && quizStarted) {
            const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [quizOptions.mode, timeLeft, quizStarted]);

    useEffect(() => {
        if (quizStarted && !currentQuestion && !quizEnded) {
            generateQuestion();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizStarted, currentQuestion, quizEnded]);

    useEffect(() => {
        if (currentQuestion && quizStarted && !quizEnded) {
            const timer = setTimeout(() => {
                handlePlayChord();
            }, 300);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion, quizStarted, quizEnded]);

    return (
        <>
            <SEO {...SEO_METADATA.DRILL_CHORDS} />
            <div ref={wrapperRef} className={styles.chordsDrillWrapper}>
            <div className={styles.chordsDrillHeader}>
                <div className={styles.titleSection}>Chords Drill</div>

                {!quizStarted && (
                    <div className={styles.headerTop}>
                        <button
                            onClick={handleStart}
                            disabled={disableStart()}
                            className={styles.startQuizButton}
                        >
                            Start Quiz
                        </button>
                    </div>
                )}

                {quizStarted && !quizEnded && (
                    <div className={styles.quizControlsPanel}>
                        {(quizOptions.mode !== "endless") && (
                            <div className={styles.quizStatus}>
                                {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                                {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                                 questionCount < quizOptions.questionLimit && (
                                    <div className={styles.statusItem}>📝 {questionCount + 1}/{quizOptions.questionLimit}</div>
                                )}
                            </div>
                        )}
                        <div className={styles.quizActions}>
                            {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                              (quizOptions.mode === "time" && timeLeft > 0) ||
                              (quizOptions.mode === "endless" && !quizEnded)) && (
                                <button className={styles.playButton} onClick={() => handlePlayChord()}>
                                    Play
                                </button>
                            )}
                            <button onClick={endQuiz} className={styles.endQuizButton}>
                                End Quiz
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.quizAndPlaybackOpts}>
                {quizStarted ? (
                    <ChordQuiz
                        {...quizOptions}
                        playbackOptions={playbackOptions}
                        currentQuestion={currentQuestion}
                        setCurrentQuestion={setCurrentQuestion}
                        questionCount={questionCount}
                        setQuestionCount={setQuestionCount}
                        attemptedQuestions={attemptedQuestions}
                        setAttemptedQuestions={setAttemptedQuestions}
                        score={score}
                        setScore={setScore}
                        timeLeft={timeLeft}
                        setTimeLeft={setTimeLeft}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        generateQuestion={generateQuestion}
                        quizEnded={quizEnded}
                        setQuizEnded={setQuizEnded}
                        resetQuiz={resetQuiz}
                    />
                ) : (
                    <div className={styles.preQuizWithBanner}>
                        <LearnMoreBanner
                            to={ROUTES.LEARN.CHORDS}
                            variant="intro"
                            introTitle="What are chords?"
                            introText={
                                <>
                                    A <strong>chord</strong> is multiple notes played together.
                                    Each chord quality has a distinct emotional color—major sounds bright,
                                    minor sounds dark, diminished sounds tense. The difference comes from
                                    the intervals between the notes.
                                </>
                            }
                            examplesLabel="Chord qualities and their character:"
                            examples={[
                                { primary: "Happy, bright", secondary: "Major" },
                                { primary: "Sad, dark", secondary: "Minor" },
                                { primary: "Tense, unstable", secondary: "Diminished" },
                            ]}
                        />
                        <ChordPreQuiz setQuizOptions={setQuizOptions} initialOptions={quizOptions} />
                    </div>
                )}

                <PlaybackOptionsPanel
                    initialOptions={playbackOptions}
                    onChange={setPlaybackOptions}
                />
            </div>

            {!quizStarted && (
                <div className={styles.startQuizFooter}>
                    <button
                        onClick={handleStart}
                        disabled={disableStart()}
                        className={styles.startQuizButton}
                    >
                        Start Quiz
                    </button>
                </div>
            )}

            {quizStarted && !quizEnded && (
                <div className={styles.quizControlsFooter}>
                    {(quizOptions.mode !== "endless") && (
                        <div className={styles.quizStatus}>
                            {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                            {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                             questionCount < quizOptions.questionLimit && (
                                <div className={styles.statusItem}>📝 {questionCount + 1}/{quizOptions.questionLimit}</div>
                            )}
                        </div>
                    )}
                    <div className={styles.quizActions}>
                        {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                          (quizOptions.mode === "time" && timeLeft > 0) ||
                          (quizOptions.mode === "endless" && !quizEnded)) && (
                            <button className={styles.playButton} onClick={() => handlePlayChord()}>
                                Play
                            </button>
                        )}
                        <button onClick={endQuiz} className={styles.endQuizButton}>
                            End Quiz
                        </button>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}
